import React, { useState, useEffect } from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import * as XLSX from 'xlsx';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from '@/components/ui/toast';
import { Download, FileText } from 'lucide-react';
import moment from 'moment'


const EXPORT_ALL_CLIENTS_REPORT = gql`
query exportAllClientsReport(
  $selectedPrograms: [String]!
  $startDate: Date
  $endDate: Date
) {
  exportAllClientsReport(
    programs: $selectedPrograms
    startDate: $startDate
    endDate: $endDate
  ) {entries{
    ODRNumber
    programNumber
    cid        
    firstName
    lastName
    medcallStatus
    cin
    disposition
    program
    releasedOn
    createdDate
  }}
}
`;

const isOdd = (i:number) => i % 2 === 1;

const CustomDate = (props:any) => {
    const { value } = props;
    const date = new window.Date(value);
    return <div>{moment(date).format('MM-DD-YYYY')}</div>;
}

export const ExportAllClientsReport: React.FC = (): any => {
  const [selectedPrograms, setSelectedPrograms] = useState<any[]>([]);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [reportData, setReportData] = useState<any[]>([]);

  const [loadReport, { data, loading }] = useLazyQuery(EXPORT_ALL_CLIENTS_REPORT);

  useEffect(() => {
    if (!data) return;
    setReportData(data.exportAllClientsReport.entries);
  }, [data]);

  const handleProgramCheckboxChange = (program: any): any => {
    setSelectedPrograms((prev: any[]) => {
      if (prev.includes(program)) return prev.filter((item: any) => item !== program);
      return [...prev, program];
    });
  };

  const handleGenerateReport = (): any => {
    setReportData([]);
    loadReport({ variables: { selectedPrograms, startDate, endDate } });
  };

  const handleDownloadExcel = (): any => {
    // Logic to convert reportData to Excel format and initiate download
    const csvData = createCSVString(reportData);
    const ws = XLSX.read(csvData, { type: 'binary' }).Sheets.Sheet1;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
    XLSX.writeFile(wb, 'exported_report.xlsx');  // Use .xlsx extension for Excel files
    toast.success('Excel file downloaded');
  };

  const handleDownloadCSV = (): any => {
    // Logic to convert reportData to CSV format and initiate download
    const csvData = createCSVString(reportData);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'exported_report.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('CSV downloaded');
  };

  const createCSVString = (data: any[]) => {
    if (data.length === 0) return '';
    const keys = Object.keys(data[0]);
    const filteredKeys = keys.filter((key) => key !== '__typename');
    const headerRow = filteredKeys.join(',');
    
    const dataRows = data.map((item) =>
      filteredKeys.map((key) => {
        if (key == 'releasedOn' || key == 'createdDate') {
          return moment(item[key]).format('MM-DD-YYYY');
        }
        return item[key];
      }).join(',')
    );
  
    return [headerRow, ...dataRows].join('\n');
  };
  
  const programOptions = ['mist', 'fist', 'dsh', 'odr', 'prgwmn', 'lead'];

  return (
    <Card className="p-6">
      <h1 className="text-xl font-semibold mb-4">Export All Clients Report</h1>

      <div className="flex flex-wrap gap-4 mb-4">
        {programOptions.map((program: any) => (
          <label key={program} className="flex items-center space-x-2">
            <Checkbox
              checked={selectedPrograms.includes(program)}
              onCheckedChange={() => handleProgramCheckboxChange(program)}
            />
            <span>{program.toUpperCase()}</span>
          </label>
        ))}
      </div>

      <div className="flex space-x-4 mb-4">
        <DatePicker
          date={startDate}
          setDate={(date: any) => setStartDate(date)}
          label="Start Date"
        />
        <DatePicker
          date={endDate}
          setDate={(date: any) => setEndDate(date)}
          label="End Date"
        />
      </div>

      <div className="flex space-x-2 mb-6">
        <Button onClick={handleGenerateReport} disabled={loading}>
          Generate Report
        </Button>
        <Button onClick={handleDownloadCSV} disabled={reportData.length === 0}>
          <FileText className="mr-2 h-4 w-4" /> CSV
        </Button>
        <Button onClick={handleDownloadExcel} disabled={reportData.length === 0}>
          <Download className="mr-2 h-4 w-4" /> XLSX
        </Button>
      </div>

      {/* Display Report Data */}
      {loading && <p>Loading...</p>}
      {reportData.length > 0 && (
        <div className="overflow-auto">
          <div className="grid grid-cols-11 gap-4 font-medium border-b pb-2">
            <div>ODRNumber</div>
            <div>Participant ID</div>
            <div>CID</div>              
            <div>First Name</div>
            <div>Last Name</div>
            <div>CIN</div>
            <div>Disposition</div>
            <div>Medcall Status</div>
            <div>Program</div>
            <div>Release Date</div>
            <div>Created Date</div>
          </div>

          {reportData.map((item: any, idx: number) => (
            <div
              key={item.CID}
              className={`grid grid-cols-11 gap-4 py-2 ${
                idx % 2 === 1 ? 'bg-gray-50' : ''
              }`}
            >
              <div>{item.ODRNumber}</div>
              <div>{item.programNumber}</div>
              <div>{item.cid}</div>                
              <div>{item.firstName}</div>
              <div>{item.lastName}</div>
              <div>{item.cin}</div>
              <div>{item.disposition}</div>
              <div>{item.medcallStatus}</div>
              <div>{item.program}</div>
              <CustomDate value={item.releasedOn}></CustomDate>
              <CustomDate value={item.createdDate}></CustomDate>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};